import { useEffect, useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye } from "lucide-react";
import { api, formatRupiah, imageUrl } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const STATUSES = ["Menunggu Pembayaran", "Lunas", "Dikirim", "Selesai", "Dibatalkan"];

const STATUS_STYLE = {
  "Menunggu Pembayaran": "bg-yellow-100 text-yellow-800",
  Lunas: "bg-green-100 text-green-800",
  Dikirim: "bg-blue-100 text-blue-800",
  Selesai: "bg-emerald-100 text-emerald-800",
  Dibatalkan: "bg-red-100 text-red-700",
};

export default function OrdersAdmin() {
  const newOrders = useOutletContext();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const load = () =>
    api
      .get("/orders")
      .then((r) => setOrders(r.data))
      .catch(() => toast.error("Gagal memuat pesanan"))
      .finally(() => setLoading(false));

  useEffect(() => {
    load().then(() => newOrders?.markSeen?.());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setStatus = async (order, status) => {
    try {
      await api.patch(`/orders/${order.id}/status`, { status });
      toast.success(`Status ${order.order_code} → ${status}`);
      load();
      if (detail?.id === order.id) setDetail((d) => ({ ...d, status }));
    } catch {
      toast.error("Gagal memperbarui status");
    }
  };

  return (
    <div data-testid="orders-admin">
      <div className="mb-8">
        <h1 className="font-serif-display text-3xl font-bold text-[#1E2B1D]">Pesanan Masuk</h1>
        <p className="text-xs text-[#6B7C66] mt-1">
          Pantau pesanan pembeli, periksa bukti transfer, dan perbarui status pengiriman.
        </p>
      </div>

      <div className="bg-[#FBF9F5] rounded-2xl border border-[#E8E2D5] overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E8E2D5] text-left text-xs uppercase tracking-wider text-[#7E8F7A]">
              <th className="p-4">Kode</th>
              <th className="p-4">Pemesan</th>
              <th className="p-4">Total</th>
              <th className="p-4">Bukti</th>
              <th className="p-4">Status</th>
              <th className="p-4 text-right">Detail</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" className="p-8 text-center text-[#6B7C66]" data-testid="orders-loading">
                  Memuat pesanan...
                </td>
              </tr>
            ) : orders.length === 0 ? (
              <tr>
                <td colSpan="6" className="p-8 text-center text-[#6B7C66]" data-testid="orders-empty">
                  Belum ada pesanan masuk.
                </td>
              </tr>
            ) : (
              orders.map((o) => (
                <tr
                  key={o.id}
                  data-testid={`order-row-${o.id}`}
                  className={`border-b border-[#E8E2D5] last:border-0 ${o.seen === false ? "bg-[#FFF8EC]" : ""}`}
                >
                  <td className="p-4 font-mono font-bold text-[#3D5A40]">
                    {o.order_code}
                    {o.seen === false && (
                      <span
                        data-testid={`order-new-badge-${o.id}`}
                        className="ml-2 text-[10px] font-sans font-bold uppercase tracking-wider bg-[#E8B4B4] text-[#3A1F1F] px-2 py-0.5 rounded-full"
                      >
                        Baru
                      </span>
                    )}
                  </td>
                  <td className="p-4">
                    <p className="font-bold text-[#1E2B1D]">{o.buyer_name}</p>
                    <p className="text-xs text-[#7E8F7A]">{o.phone}</p>
                  </td>
                  <td className="p-4 font-semibold text-[#203120]">{formatRupiah(o.total)}</td>
                  <td className="p-4">
                    {o.payment_status === "terverifikasi" ? (
                      <span data-testid={`order-payment-${o.id}`} className="text-xs font-semibold text-[#2E4530] bg-[#DCEBD9] px-2.5 py-1 rounded-full">Terverifikasi</span>
                    ) : o.payment_status === "ditolak" ? (
                      <span data-testid={`order-payment-${o.id}`} className="text-xs font-semibold text-[#7A2E2E] bg-[#F1D9D9] px-2.5 py-1 rounded-full">Ditolak</span>
                    ) : o.proof_url ? (
                      <Link to="/admin/payments" data-testid={`order-payment-${o.id}`} className="text-xs font-semibold text-[#6B5A14] bg-[#F6EBC8] px-2.5 py-1 rounded-full hover:underline">
                        Perlu Verifikasi
                      </Link>
                    ) : (
                      <span data-testid={`order-payment-${o.id}`} className="text-xs text-[#7E8F7A]">Belum ada</span>
                    )}
                  </td>
                  <td className="p-4">
                    <Select value={o.status} onValueChange={(v) => setStatus(o, v)}>
                      <SelectTrigger
                        data-testid={`order-status-${o.id}`}
                        className={`w-44 rounded-full border-0 text-xs font-semibold ${STATUS_STYLE[o.status] || ""}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => (
                          <SelectItem key={s} value={s} data-testid={`status-option-${s.replace(/\s/g, "-")}`}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-4 text-right">
                    <button
                      onClick={() => setDetail(o)}
                      data-testid={`order-detail-${o.id}`}
                      className="p-2 rounded-lg border border-[#D5CCBC] text-[#3D5A40] hover:bg-[#F4EFE6] transition-colors"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="bg-[#FBF9F5] border border-[#DACFBE] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-xl text-[#1E2B1D]">
              Detail Pesanan {detail?.order_code}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm" data-testid="order-detail-modal">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="bg-[#F4EFE6] rounded-xl p-3">
                  <span className="text-[#7E8F7A] block">Pemesan</span>
                  <span className="font-bold text-[#1E2B1D]">{detail.buyer_name}</span>
                </div>
                <div className="bg-[#F4EFE6] rounded-xl p-3">
                  <span className="text-[#7E8F7A] block">Kontak</span>
                  <span className="font-bold text-[#1E2B1D]">{detail.phone}</span>
                </div>
              </div>
              <div className="bg-[#F4EFE6] rounded-xl p-3 text-xs">
                <span className="text-[#7E8F7A] block mb-1">Alamat Pengiriman</span>
                <span className="text-[#1E2B1D]">{detail.address}</span>
              </div>
              {detail.note && (
                <div className="bg-[#F4EFE6] rounded-xl p-3 text-xs">
                  <span className="text-[#7E8F7A] block mb-1">Kartu Ucapan</span>
                  <span className="italic text-[#1E2B1D]">"{detail.note}"</span>
                </div>
              )}
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-[#354634] block mb-2">
                  Item Pesanan
                </span>
                <ul className="space-y-1.5 text-xs text-[#354634]">
                  {detail.items.map((i, idx) => (
                    <li key={idx} className="flex justify-between">
                      <span>
                        {i.name} <span className="text-[#7E8F7A]">× {i.qty}</span>
                      </span>
                      <span className="font-semibold">{formatRupiah(i.price * i.qty)}</span>
                    </li>
                  ))}
                  <li className="flex justify-between pt-2 border-t border-[#E0D7C6] font-bold text-[#203120]">
                    <span>Total</span>
                    <span>{formatRupiah(detail.total)}</span>
                  </li>
                </ul>
              </div>
              {detail.proof_url && (
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-[#354634] block mb-2">
                    Bukti Transfer
                  </span>
                  <a href={imageUrl(detail.proof_url)} target="_blank" rel="noreferrer">
                    <img
                      src={imageUrl(detail.proof_url)}
                      alt="Bukti transfer"
                      data-testid="order-proof-image"
                      className="rounded-xl border border-[#E8E2D5] max-h-64 object-contain bg-white"
                    />
                  </a>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
